import React, { memo, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { TextField } from 'transfix-ui/components/TextField'
import { makeStyles } from 'transfix-ui/core'
import {
  getApiUrl,
  getGraphqlUrl,
  setCustomApiHostnameOverride,
  clearApiHostnameOverride,
} from 'utils/api/apiHelpers'

const useStyles = makeStyles(theme => ({
  input: {
    position: 'fixed',
    zIndex: 99,
    paddingRight: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      width: '60%',
      paddingRight: '30px',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      width: '90%',
    },
    [theme.breakpoints.up('md')]: {
      width: '40%',
      paddingRight: '20px',
    },
  },
}))

export const Api = memo(() => {
  const styles = useStyles()

  const [apiUrl, setApiUrl] = useState(getApiUrl('MARKETPLACE'))
  const [graphqlUrl, setGraphqlUrl] = useState(getGraphqlUrl('MARKETPLACE'))
  const [backendHostname, setBackendHostname] = useState(new URL(apiUrl).hostname)
  const [validationMessage, setValidationMessage] = useState('')

  const updateBackendHostnameHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (__PROD__) {
      setValidationMessage('Cannot change API hostname in production')
    }

    const hostname = event.currentTarget.value

    if (hostname.match(/^[a-z0-9-]+\.transfix\.io$/i)) {
      setBackendHostname(event.currentTarget.value)
      setValidationMessage('')
    } else {
      setValidationMessage('Enter a valid transfix.io hostname eg. staging.transfix.io')
    }
  }

  const applyApiHostnameHandler = () => {
    setCustomApiHostnameOverride('MARKETPLACE', backendHostname)
    setApiUrl(getApiUrl('MARKETPLACE'))
    setGraphqlUrl(getGraphqlUrl('MARKETPLACE'))
    setValidationMessage('Updated')
  }

  const clearApiHostnameHandler = () => {
    clearApiHostnameOverride('MARKETPLACE')
    setApiUrl(getApiUrl('MARKETPLACE'))
    setGraphqlUrl(getGraphqlUrl('MARKETPLACE'))
    setValidationMessage('Updated')
  }

  if (__PROD__) {
    return (
      <div>
        <p>Not for production use. Meant for switching to alt staging backends in staging/dev.</p>
      </div>
    )
  }

  return (
    <>
      <div>
        <p className='body-medium'>Current API URLs </p>
        <ul>
          <li>REST API: {apiUrl}</li>
          <li>GraphQL: {graphqlUrl}</li>
        </ul>
        <p>
          <Button variant='text' onClick={clearApiHostnameHandler} className='pa0'>
            Reset
          </Button>
        </p>
        <p>You can use one of the following staging backends:</p>
        <p>
          <ul>
            <li>staging.transfix.io</li>
            <li>new-staging-1.transfix.io</li>
            <li>new-staging-2.transfix.io</li>
          </ul>
        </p>
        <p className='text-small'>NOTE: does not work for FMS/TMS use cases</p>
      </div>
      <hr />
      {/* TODO: switch to pre-defined values once alt staging domains are solidified */}
      <div>
        <p className='body-medium'>Change staging hostname</p>
        <TextField
          className={styles.input}
          placeholder={backendHostname}
          onChange={updateBackendHostnameHandler}
          type='text'
          helperMessage='Enter the hostname of the staging API backend eg. new-staging-2.transfix.io'
          validationMessage={validationMessage}
        />
      </div>
      <div className='mt4'>
        <p>{validationMessage}</p>
        <Button variant='text' onClick={applyApiHostnameHandler} className='pa0'>
          Apply
        </Button>
      </div>
    </>
  )
})
